/**
 * Apple HealthKit Native Bridge & Telemetry Module
 * Handles WKScriptMessageHandler communication, payload decoding, normalization, and internal event dispatching.
 *
 * @module integrations/healthkit
 */

import { store } from '../../store.js';
import {
  isGenericStageName,
  normalizeStationName,
  parseSequentialStationsFromNotes
} from '../telemetry/index.js';


/**
 * Maps Apple HealthKit workoutActivityType identifiers to PulseFit workout types.
 * @param {number|string} activityType
 * @returns {string} PulseFit workout activity type
 */
export function mapHealthKitActivityType(activityType) {
  const code = Number(activityType);
  switch (code) {
    case 37: return 'Run';       // HKWorkoutActivityTypeRunning
    case 13: return 'Cycle';     // HKWorkoutActivityTypeCycling
    case 46: return 'Swim';      // HKWorkoutActivityTypeSwimming
    case 52: return 'Walk';      // HKWorkoutActivityTypeWalking
    case 20: return 'Strength';  // HKWorkoutActivityTypeFunctionalStrengthTraining
    case 50: return 'Strength';  // HKWorkoutActivityTypeTraditionalStrengthTraining
    case 14: return 'Cardio';    // HKWorkoutActivityTypeCrossTraining
    case 24: return 'Cardio';    // HKWorkoutActivityTypeHighIntensityIntervalTraining
    case 57: return 'Yoga';      // HKWorkoutActivityTypeYoga
    default: return 'Other';
  }
}

/**
 * Normalizes a raw HealthKit workout dictionary into the standard PulseFit workout model.
 *
 * @param {Object} hw - Raw HealthKit workout payload dictionary
 * @returns {Object} Normalized workout object
 */
export function normalizeHealthKitWorkout(hw) {
  if (!hw || typeof hw !== 'object') {
    throw new TypeError('normalizeHealthKitWorkout requires a valid object');
  }

  const rawStart = hw.startDate || hw.start_date || hw.start_time || hw.startTime || hw.timestamp || Date.now();
  const startDate = new Date(rawStart);
  const startMs = isNaN(startDate.getTime()) ? Date.now() : startDate.getTime();
  const dateObj = new Date(startMs);

  const dateFormatted = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timeFormatted = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // Duration in minutes
  let durationMin = 0;
  if (Number.isFinite(hw.duration)) {
    durationMin = Math.round((hw.duration / 60) * 10) / 10;
  } else if (Number.isFinite(hw.duration_seconds)) {
    durationMin = Math.round((hw.duration_seconds / 60) * 10) / 10;
  } else if (hw.endDate || hw.end_date) {
    const endMs = new Date(hw.endDate || hw.end_date).getTime();
    if (endMs > startMs) {
      durationMin = Math.round(((endMs - startMs) / 60000) * 10) / 10;
    }
  }
  if (durationMin <= 0) durationMin = 30; // fallback minimum

  // Calories (totalEnergyBurned can be number or object { value, unit })
  let calories = 0;
  if (Number.isFinite(hw.totalEnergyBurned)) {
    calories = Math.round(hw.totalEnergyBurned);
  } else if (hw.totalEnergyBurned && Number.isFinite(hw.totalEnergyBurned.value)) {
    calories = Math.round(hw.totalEnergyBurned.value);
  } else if (Number.isFinite(hw.total_energy_burned_kcal)) {
    calories = Math.round(hw.total_energy_burned_kcal);
  } else if (Number.isFinite(hw.total_calories)) {
    calories = Math.round(hw.total_calories);
  } else if (Number.isFinite(hw.calories)) {
    calories = Math.round(hw.calories);
  }

  // Distance in kilometers (totalDistance can be meters or object { value, unit })
  let distanceKm = null;
  if (Number.isFinite(hw.totalDistance)) {
    const distMeters = hw.totalDistance > 50 ? hw.totalDistance : hw.totalDistance * 1000;
    distanceKm = Math.round((distMeters / 1000) * 100) / 100;
  } else if (hw.totalDistance && Number.isFinite(hw.totalDistance.value)) {
    const distMeters = hw.totalDistance.value > 50 ? hw.totalDistance.value : hw.totalDistance.value * 1000;
    distanceKm = Math.round((distMeters / 1000) * 100) / 100;
  } else if (Number.isFinite(hw.total_distance_meters)) {
    distanceKm = Math.round((hw.total_distance_meters / 1000) * 100) / 100;
  } else if (Number.isFinite(hw.distance)) {
    const distMeters = hw.distance > 50 ? hw.distance : hw.distance * 1000;
    distanceKm = Math.round((distMeters / 1000) * 100) / 100;
  }

  let type = mapHealthKitActivityType(hw.workoutActivityType || hw.activity_type);
  const rawTypeStr = typeof hw.type === 'string' ? hw.type : (typeof hw.activity_type === 'string' && isNaN(Number(hw.activity_type)) ? hw.activity_type : '');
  if (type === 'Other' && rawTypeStr) {
    type = rawTypeStr;
  }
  const customStr = `${hw.customWorkoutType || ''} ${hw.custom_title || ''} ${hw.workoutNotes || ''} ${hw.notes || ''} ${hw.name || ''}`;
  if (/hyrox|roxkit/i.test(rawTypeStr) || /hyrox|roxkit/i.test(customStr)) {
    type = 'Hyrox Training';
  }

  const deviceName = (hw.device && hw.device.name) || hw.sourceName || (hw.sourceRevision && hw.sourceRevision.source && hw.sourceRevision.source.name) || 'Apple Watch';
  const rawUuid = hw.uuid || hw.id || `hk-${startMs}`;
  const uuid = typeof rawUuid === 'string' ? rawUuid.toLowerCase() : String(rawUuid).toLowerCase();

  const avgHeartrate = Number.isFinite(hw.avgHeartRate) ? Math.round(hw.avgHeartRate) : (Number.isFinite(hw.average_heart_rate) ? Math.round(hw.average_heart_rate) : (Number.isFinite(hw.avg_heartrate) ? Math.round(hw.avg_heartrate) : null));
  const maxHeartrate = Number.isFinite(hw.maxHeartRate) ? Math.round(hw.maxHeartRate) : (Number.isFinite(hw.max_heart_rate) ? Math.round(hw.max_heart_rate) : (Number.isFinite(hw.max_heartrate) ? Math.round(hw.max_heartrate) : null));

  const rawNotesText = hw.workoutNotes || hw.notes || (hw.healthkit_raw_metadata && hw.healthkit_raw_metadata.notes) || '';
  const notesStations = parseSequentialStationsFromNotes(rawNotesText);

  // Parse station splits / stages if available
  const stages = [];
  const rawSplits = hw.station_splits || hw.workoutActivities;
  if (Array.isArray(rawSplits)) {
    rawSplits.forEach((act, idx) => {
      const rawActTypeLower = String(act.type || act.stage_type || '').toLowerCase();
      const isExplicitNonStation = rawActTypeLower === 'run' || rawActTypeLower === 'roxzone' || rawActTypeLower === 'rest' || rawActTypeLower === 'cardio';
      const isExplicitStation = rawActTypeLower === 'station' || rawActTypeLower === 'strength';
      const defaultNonStationName = rawActTypeLower === 'roxzone' ? 'RoxZone' : (rawActTypeLower === 'rest' ? 'Rest' : (rawActTypeLower === 'cardio' ? 'Cardio' : (rawActTypeLower === 'run' ? 'Run' : '')));
      let stageName = act.station_name || act.stationName || act.name || act.customLabel || act.activityType || (isExplicitNonStation ? defaultNonStationName : 'Hyrox Station');
      if (isExplicitNonStation && isGenericStageName(stageName)) {
        stageName = defaultNonStationName;
      } else if (!isExplicitNonStation && isGenericStageName(stageName) && notesStations[idx] && !isGenericStageName(notesStations[idx])) {
        stageName = notesStations[idx];
      }
      const trimmedStageName = stageName.trim();
      const segmentDuration = act.duration_seconds !== undefined ? act.duration_seconds : (act.duration || 0);
      const isRun = (!isExplicitStation && /^(?:run|running)(?:\b|\s*:)/i.test(trimmedStageName)) || rawActTypeLower === 'run';
      const isRox = trimmedStageName.toLowerCase().includes('rox') || rawActTypeLower === 'roxzone';
      const isRest = (!isExplicitStation && /^rest(?:\s+period)?$/i.test(trimmedStageName)) || rawActTypeLower === 'rest';
      const isCardio = trimmedStageName.toLowerCase().includes('cardio') || rawActTypeLower === 'cardio';
      const isStrength = trimmedStageName.toLowerCase().includes('strength') || rawActTypeLower === 'strength';

      let stageType = 'Station';
      let stationName = stageName;
      if (isRun) {
        stageType = 'Run';
        stationName = stageName;
      } else if (isRox) {
        stageType = 'RoxZone';
        stationName = 'RoxZone';
      } else if (isRest) {
        stageType = 'Rest';
        stationName = 'Rest';
      } else if (isCardio) {
        stageType = 'Cardio';
        stationName = stageName;
      } else if (isStrength) {
        stageType = 'Strength';
        stationName = stageName;
      }

      const totalSec = Math.round(segmentDuration);
      const durationMinStage = Math.floor(totalSec / 60);
      const durationSecStage = totalSec % 60;
      const avgHrStage = act.average_heart_rate !== undefined ? Math.round(act.average_heart_rate) : (act.avgHr ? Math.round(act.avgHr) : 0);

      stages.push({
        stageNum: idx + 1,
        type: stageType,
        stationName: stationName,
        durationMin: durationMinStage,
        durationSec: durationSecStage,
        avgHr: avgHrStage,
        distance: act.distance !== undefined ? act.distance : null,
        reps: act.reps !== undefined ? act.reps : null,
        weight: act.weight !== undefined ? act.weight : null
      });
    });
  }

  let fallbackStages = null;
  if (stages.length === 0 && Array.isArray(hw.stages)) {
    fallbackStages = hw.stages.map((st, idx) => {
      const rawTypeLower = String(st.type || st.stage_type || '').toLowerCase();
      const isExplicitNonStation = rawTypeLower === 'run' || rawTypeLower === 'roxzone' || rawTypeLower === 'rest' || rawTypeLower === 'cardio';
      const defaultNonStationName = rawTypeLower === 'roxzone' ? 'RoxZone' : (rawTypeLower === 'rest' ? 'Rest' : (rawTypeLower === 'cardio' ? 'Cardio' : (rawTypeLower === 'run' ? 'Run' : '')));
      const currentName = st.stationName || st.station_name || st.name || (isExplicitNonStation ? defaultNonStationName : '');
      const stageIdx = (st.stageNum || st.stage_index || (idx + 1)) - 1;
      const recovered = (!isExplicitNonStation && isGenericStageName(currentName) && notesStations[stageIdx] && !isGenericStageName(notesStations[stageIdx]))
        ? notesStations[stageIdx]
        : currentName;
      if (recovered && recovered !== (st.stationName || st.name || '')) {
        return {
          ...st,
          name: recovered,
          stationName: recovered
        };
      }
      return st;
    });
  }

  const finalStages = stages.length > 0 ? stages : fallbackStages;

  if (finalStages && finalStages.length > 0 && (type === 'Other' || type === 'Cardio')) {
    type = 'Hyrox Training';
  }

  if ((!distanceKm || distanceKm === 0) && finalStages && finalStages.length > 0) {
    const totalStageMeters = finalStages.reduce((acc, st) => {
      const typeLower = (st.type || st.stage_type || '').toLowerCase();
      const nameLower = (st.stationName || st.name || '').toLowerCase();
      const isRun = typeLower === 'run' || typeLower === 'running' || typeLower === 'cardio';
      const isRox = typeLower === 'roxzone' || nameLower.includes('rox');
      if ((isRun || isRox) && typeLower !== 'rest') {
        const dist = Number(st.distance_meters ?? st.distance ?? 0);
        return acc + (Number.isFinite(dist) && dist > 0 ? dist : 0);
      }
      return acc;
    }, 0);
    if (totalStageMeters > 0) {
      distanceKm = Math.round((totalStageMeters / 1000) * 100) / 100;
    }
  }

  return {
    id: uuid,
    timestamp: startMs,
    date: dateFormatted,
    time: timeFormatted,
    type: type,
    duration: durationMin,
    calories: calories,
    distance: distanceKm,
    source: 'HealthKit',
    healthKitId: uuid,
    sourceId: uuid,
    deviceName: deviceName,
    avgHeartrate: avgHeartrate,
    maxHeartrate: maxHeartrate,
    notes: hw.workoutNotes || hw.notes || `Imported from ${deviceName}`,
    stationSequenceNotes: (rawNotesText && (rawNotesText.includes('➔') || rawNotesText.includes('->'))) ? rawNotesText : null,
    rawHealthKitPayloadJSON: JSON.stringify(hw),
    stages: finalStages,
    excludeFromStats: false
  };
}

/**
 * Safely parses raw incoming HealthKit payload (either parsed JS Array, single object, or JSON string).
 * @param {string|Array<Object>|Object} payload
 * @returns {Array<Object>}
 */
export function parseHealthKitPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload === 'object') return [payload];
  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (_) {
      return [];
    }
  }
  return [];
}

/**
 * Outbound dispatch requesting HealthKit workouts from native iOS WKWebView bridge.
 * @param {Object} [options={}]
 * @param {string} [options.date]
 * @param {string} [options.startDate]
 * @returns {boolean} Whether the message was successfully dispatched
 */
export function requestHealthKitWorkouts(options = {}) {
  const hasNativeBridge = typeof window !== 'undefined' &&
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.healthKitHandler;

  if (!hasNativeBridge) {
    if (typeof console !== 'undefined') {
      console.log('🍎 [HealthKit] Native iOS bridge not detected — skipping fetch (running in browser environment).');
    }
    return false;
  }

  try {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);

    const payload = {
      action: 'fetchNativeWorkouts',
      date: options.date || today.toISOString(),
      startDate: options.startDate || weekAgo.toISOString()
    };

    window.webkit.messageHandlers.healthKitHandler.postMessage(payload);
    return true;
  } catch (e) {
    console.warn('🍎 [HealthKit] Failed to dispatch request to native bridge:', e);
    return false;
  }
}

let lastSummaryRequestTime = 0;
const SUMMARY_DEBOUNCE_MS = 500;

export function resetSummaryDebounce() {
  lastSummaryRequestTime = 0;
}

/**
 * Outbound dispatch requesting HealthKit daily health summary (HRV, Sleep, Resting HR).
 * Includes 500ms sliding debounce window to guard against duplicate rapid triggers.
 * @param {string} [date] - ISO string or YYYY-MM-DD
 * @param {Object} [options={}] - Options (e.g. { force: true })
 * @returns {boolean}
 */
export function requestHealthKitSummary(date, options = {}) {
  const hasNativeBridge = typeof window !== 'undefined' &&
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.healthKitHandler;

  if (!hasNativeBridge) return false;

  const now = Date.now();
  if (!options.force && (now - lastSummaryRequestTime < SUMMARY_DEBOUNCE_MS)) {
    return false;
  }
  lastSummaryRequestTime = now;

  try {
    const dateStr = date || new Date().toISOString();
    window.webkit.messageHandlers.healthKitHandler.postMessage({
      action: 'fetchHealthSummary',
      date: dateStr
    });
    return true;
  } catch (e) {
    console.warn('🍎 [HealthKit] Failed to dispatch summary request to native bridge:', e);
    return false;
  }
}

/**
 * Normalizes and dispatches incoming HealthKit daily summary payload.
 * Guarantees restingHr and restingHR casing parity and dispatches pulsefit:healthkit-summary.
 * @param {string|Object} summaryPayload
 * @returns {Object|null}
 */
export function handleIncomingHealthSummary(summaryPayload) {
  let parsed = summaryPayload;
  if (typeof summaryPayload === 'string') {
    try {
      parsed = JSON.parse(summaryPayload);
    } catch (_) {
      return null;
    }
  }

  if (!parsed || typeof parsed !== 'object') return null;

  // Casing parity normalization for resting heart rate
  if (parsed.restingHR !== undefined && parsed.restingHr === undefined) {
    parsed.restingHr = parsed.restingHR;
  } else if (parsed.restingHr !== undefined && parsed.restingHR === undefined) {
    parsed.restingHR = parsed.restingHr;
  }

  // Sleep score normalization & boundary guard (0-100)
  if (parsed.sleep_score !== undefined && parsed.sleepScore === undefined) {
    parsed.sleepScore = parsed.sleep_score;
  }
  if (parsed.sleepScore !== undefined && parsed.sleepScore !== null && !isNaN(parsed.sleepScore)) {
    parsed.sleepScore = Math.min(100, Math.max(0, Math.round(Number(parsed.sleepScore))));
  }

  if (typeof window !== 'undefined') {
    const event = new CustomEvent('pulsefit:healthkit-summary', {
      detail: parsed
    });
    window.dispatchEvent(event);
  }

  return parsed;
}

/**
 * Outbound dispatch requesting detailed second-by-second HealthKit workout telemetry.
 * @param {Object} options
 * @returns {boolean}
 */
export function requestDetailedWorkout(options = {}) {
  const hasNativeBridge = typeof window !== 'undefined' &&
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.healthKitHandler;

  if (!hasNativeBridge) return false;

  try {
    window.webkit.messageHandlers.healthKitHandler.postMessage({
      action: 'fetchDetailedWorkout',
      startDate: options.startDate,
      endDate: options.endDate,
      startTimestamp: options.startTimestamp,
      endTimestamp: options.endTimestamp
    });
    return true;
  } catch (e) {
    console.warn('🍎 [HealthKit] Failed to dispatch detailed workout request:', e);
    return false;
  }
}

/**
 * Initializes HealthKit bridge listeners on window and emits typed CustomEvents.
 * @param {Object} [options={}]
 * @param {Function} [options.onWorkouts]
 * @param {Function} [options.onSummary]
 * @param {Function} [options.onLog]
 */
export function initHealthKitBridge(options = {}) {
  if (typeof window === 'undefined') return;

  const handleWorkouts = (rawPayload) => {
    const rawList = parseHealthKitPayload(rawPayload);
    const normalized = rawList.map(item => normalizeHealthKitWorkout(item));

    const event = new CustomEvent('pulsefit:healthkit-workouts', {
      detail: { workouts: normalized, raw: rawList }
    });
    window.dispatchEvent(event);

    if (typeof options.onWorkouts === 'function') {
      options.onWorkouts(normalized, rawList);
    }
  };

  const handleSummary = (summaryPayload) => {
    const parsed = handleIncomingHealthSummary(summaryPayload);
    if (typeof options.onSummary === 'function' && parsed) {
      options.onSummary(parsed);
    }
  };

  const handleLog = (message) => {
    const event = new CustomEvent('pulsefit:healthkit-log', {
      detail: message
    });
    window.dispatchEvent(event);

    if (typeof options.onLog === 'function') {
      options.onLog(message);
    }
  };

  // Bind global window bridges for native WKScriptMessageHandler calls
  window.receiveHealthKitWorkouts = handleWorkouts;
  window.ingestNativeWorkouts = handleWorkouts;
  window.receiveHealthKitSummary = handleSummary;
  window.updatePulseFitHealthData = handleSummary;
  window.updateRoxKitHealthData = handleSummary;
  window.healthKitDebugLog = handleLog;
  window.appendToHealthKitDebugLog = handleLog;
  window.startFavoriteSimulation = startFavoriteSimulation;
}

/**
 * Dispatches a favorite workout or planned routine payload to the native Apple Watch companion.
 * 
 * @param {string|Object} workoutOrId - Target workout/plan ID or object to send to Watch
 * @returns {boolean} True if successfully dispatched, false otherwise
 */
export function startFavoriteSimulation(workoutOrId) {
  if (!workoutOrId) return false;

  let targetWorkout = null;
  if (typeof workoutOrId === 'object') {
    targetWorkout = workoutOrId;
  } else {
    const workoutsList = (store && Array.isArray(store.workouts)) ? store.workouts : [];
    const plannedList = (store && Array.isArray(store.plannedWorkouts)) ? store.plannedWorkouts : [];
    targetWorkout =
      workoutsList.find(w => w && String(w.id) === String(workoutOrId)) ||
      plannedList.find(p => p && String(p.id) === String(workoutOrId)) ||
      null;
  }

  if (!targetWorkout) {
    console.warn(`🍎 [HealthKit] Workout or Planned Routine ${workoutOrId} not found for Watch dispatch.`);
    return false;
  }

  // Construct replay stages (falling back to single stage if no splits exist)
  let replayStages = [];
  if (Array.isArray(targetWorkout.stages) && targetWorkout.stages.length > 0) {
    replayStages = targetWorkout.stages.map(s => {
      const rawDuration = Math.round((s.durationMin || 0) * 60 + (s.durationSec || 0) || s.duration_seconds || s.duration || 0);
      // Enforce 15s minimum floor for timed countdowns; 0 indicates open-ended stopwatch stage
      const sanitizedDuration = rawDuration > 0 ? Math.max(15, rawDuration) : 0;
      const resolvedDistance = (s.distance !== null && s.distance !== undefined && Number(s.distance) > 0)
        ? Number(s.distance)
        : Number(s.distance_meters || s.reps || s.distance || 0);
      return {
        name: s.stationName || s.name || s.type || 'Stage',
        duration: sanitizedDuration,
        distance: resolvedDistance,
        originalAverageHR: Math.round(s.avgHr || s.averageHR || s.avg_hr || 0)
      };
    });
  } else {
    const rawTotalSec = Math.round((targetWorkout.duration || 30) * 60);
    const sanitizedDuration = rawTotalSec > 0 ? Math.max(15, rawTotalSec) : 0;
    replayStages = [{
      name: targetWorkout.name || targetWorkout.type || 'Workout Replay',
      duration: sanitizedDuration,
      distance: (targetWorkout.distance || 0) * 1000,
      originalAverageHR: Math.round(targetWorkout.avgHeartrate || 0)
    }];
  }

  const payload = {
    id: targetWorkout.id,
    name: targetWorkout.name || targetWorkout.type || 'Workout Replay',
    stages: replayStages
  };

  const hasNativeBridge = typeof window !== 'undefined' &&
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.healthKitHandler;

  if (hasNativeBridge) {
    try {
      window.webkit.messageHandlers.healthKitHandler.postMessage({
        action: 'startFavoriteSimulation',
        workout: payload
      });
      if (typeof window.showToast === 'function') {
        window.showToast("Starting workout replay on Apple Watch...", "success", 3000);
      }
      return true;
    } catch (e) {
      console.error("🍎 [HealthKit] Failed to dispatch workout to native bridge:", e);
      return false;
    }
  } else {
    // Browser mock fallback
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pulsefit:favorite-simulation-dispatched', { detail: payload }));
      if (typeof window.showToast === 'function') {
        window.showToast("Workout replay dispatched (browser simulation mode)...", "info", 3000);
      }
    }
    return true;
  }
}

if (typeof window !== 'undefined') {
  if (!window.updatePulseFitHealthData) {
    window.updatePulseFitHealthData = (payload) => handleIncomingHealthSummary(payload);
  }
  if (!window.receiveHealthKitSummary) {
    window.receiveHealthKitSummary = (payload) => handleIncomingHealthSummary(payload);
  }
}


